import { useState, useEffect, useRef } from 'react';
import PalmerNavbar from '@/pages/home/components/PalmerNavbar';
import PalmerFooter from '@/pages/home/components/PalmerFooter';

const categories = ['All', 'Agency', 'AI & Tech', 'Branding', 'Photography', 'Portfolio', 'Typography'];

const galleryItems = [
  {
    id: 1,
    category: 'AI & Tech',
    name: 'Nova Intelligence',
    subtitle: 'AI Platform',
    description: 'AI-powered decision engine for enterprise brands.',
    tags: ['AI Strategy', 'Platform', 'Enterprise'],
    bgColor: '#0a0a0a',
    textColor: '#fff',
    mainImg: 'https://framerusercontent.com/images/wA52DtSvQDx894hqLZv4ezfKfz8.png',
    featured: true,
  },
  {
    id: 2,
    category: 'Branding',
    name: 'Sonder Goods',
    subtitle: 'Brand Identity',
    description: 'A full brand system for a modern lifestyle label.',
    tags: ['Identity', 'Logo', 'System'],
    bgColor: '#111',
    textColor: '#fff',
    mainImg: 'https://framerusercontent.com/images/WSIwyrpSzX4O0fiESBwPTjSWBE.png',
    featured: false,
  },
  {
    id: 3,
    category: 'Portfolio',
    name: 'Halo Wear',
    subtitle: 'Web Design',
    description: 'Performance-driven e-commerce for a fashion-forward brand.',
    tags: ['Web', 'E-Commerce', 'Fashion'],
    bgColor: '#0d0d0d',
    textColor: '#fff',
    mainImg: 'https://framerusercontent.com/images/IhwR33YbJAKylGnbmoCW4maBHI.png',
    featured: false,
  },
  {
    id: 4,
    category: 'Photography',
    name: 'Lucent Lab',
    subtitle: 'Visual Direction',
    description: 'Editorial photography meets digital art direction.',
    tags: ['Photography', 'Art Direction'],
    bgColor: '#080808',
    textColor: '#fff',
    mainImg: 'https://framerusercontent.com/images/G891sPJdh93gPfGSBboEt88Now.png',
    featured: false,
  },
  {
    id: 5,
    category: 'Agency',
    name: 'Arc & Bloom',
    subtitle: 'Agency Website',
    description: 'A bold agency presence built for creative studios.',
    tags: ['Agency', 'Web', 'Motion'],
    bgColor: '#0a0a0a',
    textColor: '#fff',
    mainImg: 'https://framerusercontent.com/images/kSBqNFitJQuBzXuk7tl1FqlAHhs.png',
    featured: false,
  },
  {
    id: 6,
    category: 'Portfolio',
    name: 'Atelier Nara',
    subtitle: 'Portfolio',
    description: 'Minimal portfolio for a multidisciplinary creative.',
    tags: ['Portfolio', 'Minimal'],
    bgColor: '#0d0d0d',
    textColor: '#fff',
    mainImg: 'https://framerusercontent.com/images/svmMd86RbsKfib7KzvpKAUsHrk.png',
    featured: false,
  },
  {
    id: 7,
    category: 'Typography',
    name: 'Type Systems',
    subtitle: 'Type Design',
    description: 'Experimental typographic systems for editorial brands.',
    tags: ['Typography', 'Editorial'],
    bgColor: '#111',
    textColor: '#fff',
    mainImg: 'https://framerusercontent.com/images/Jt7zqgTjQMYT15YvEkLGKiF9Cw.png',
    featured: false,
  },
  {
    id: 8,
    category: 'AI & Tech',
    name: 'DataFlow Studio',
    subtitle: 'SaaS Dashboard',
    description: 'Clean, data-dense UI for a real-time analytics platform.',
    tags: ['SaaS', 'Dashboard', 'AI'],
    bgColor: '#0a0a0a',
    textColor: '#fff',
    mainImg: 'https://framerusercontent.com/images/YIi7jRxIe8p6gLtM1ZMNpJyVYs.jpeg',
    featured: false,
  },
  {
    id: 9,
    category: 'Branding',
    name: 'Monolith Studio',
    subtitle: 'Brand System',
    description: 'Brutalist brand identity for an independent design studio.',
    tags: ['Branding', 'Brutalist'],
    bgColor: '#0d0d0d',
    textColor: '#fff',
    mainImg: 'https://framerusercontent.com/images/7WVAcnCw5jrTdcET3CmMrpU7gf0.png',
    featured: false,
  },
];

function GalleryCard({ item, index, visible }: { item: typeof galleryItems[0]; index: number; visible: boolean }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="group border border-white/10 overflow-hidden cursor-pointer transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transitionDelay: `${index * 55}ms`,
        background: item.bgColor,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image area */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/10' }}>
        <img
          src={item.mainImg}
          alt={item.name}
          className="w-full h-full object-cover object-top transition-transform duration-700"
          style={{ transform: hovered ? 'scale(1.05)' : 'scale(1)' }}
        />
        {/* Overlay on hover */}
        <div
          className="absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-300"
          style={{ opacity: hovered ? 1 : 0 }}
        >
          <div className="w-10 h-10 border border-white/60 rounded-full flex items-center justify-center">
            <i className="ri-arrow-right-up-line text-white text-sm"></i>
          </div>
        </div>
        {/* Category badge */}
        <div className="absolute top-3 left-3">
          <span
            className="text-white/70 text-xs border border-white/20 px-2 py-0.5 bg-black/40 backdrop-blur-sm"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {item.category}
          </span>
        </div>
      </div>

      {/* Info row */}
      <div className="px-4 py-3 flex items-center justify-between border-t border-white/10">
        <div>
          <p
            className="text-white text-sm font-semibold leading-tight"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {item.name}
          </p>
          <p
            className="text-white/35 text-xs"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {item.subtitle}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <i
            className="ri-eye-line text-white/30 text-sm cursor-pointer hover:text-white/70 transition-colors"
          ></i>
          <i
            className="ri-arrow-right-up-line text-white/30 text-sm cursor-pointer hover:text-white/70 transition-colors"
          ></i>
        </div>
      </div>
    </div>
  );
}

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [visible, setVisible] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setHeaderVisible(true), 100);
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (gridRef.current) obs.observe(gridRef.current);
    return () => obs.disconnect();
  }, []);

  // Reset visible on category change for re-animation
  const handleCategoryChange = (cat: string) => {
    setVisible(false);
    setActiveCategory(cat);
    setTimeout(() => setVisible(true), 80);
  };

  const filtered = activeCategory === 'All'
    ? galleryItems
    : galleryItems.filter((item) => item.category === activeCategory);

  return (
    <div className="bg-black min-h-screen" style={{ fontFamily: "'DM Sans', sans-serif" }} id="top">
      <PalmerNavbar />

      {/* Hero header — Brutal Gallery style */}
      <section className="pt-28 pb-0 px-6 border-b border-white/10">
        {/* Big brutal title */}
        <div
          className="transition-all duration-700"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
          }}
        >
          <h1
            className="text-white font-black leading-none"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(56px, 12vw, 180px)',
              letterSpacing: '-0.05em',
              lineHeight: 0.88,
            }}
          >
            GALLERY
          </h1>
        </div>

        {/* Subtitle */}
        <div
          className="mt-4 mb-8 transition-all duration-700 delay-100"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          <p
            className="text-white/40 text-sm font-medium text-center lg:text-left max-w-xl"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Curated catalog of AI-driven, minimalist &amp; contemporary digital work — built by Nova AI Digital LLC.
          </p>
        </div>

        {/* Category filter pills */}
        <div
          className="flex flex-wrap gap-2 pb-6 transition-all duration-700 delay-150"
          style={{ opacity: headerVisible ? 1 : 0 }}
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className="px-4 py-1.5 text-xs border transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                borderRadius: '999px',
                background: activeCategory === cat ? 'white' : 'transparent',
                color: activeCategory === cat ? 'black' : 'rgba(255,255,255,0.45)',
                borderColor: activeCategory === cat ? 'white' : 'rgba(255,255,255,0.15)',
                fontWeight: activeCategory === cat ? 700 : 400,
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery grid */}
      <section className="px-6 py-10" ref={gridRef}>
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {filtered.map((item, i) => (
              <div key={item.id} className="bg-black">
                <GalleryCard item={item} index={i} visible={visible} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-32">
            <p className="text-white/20 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              No items in this category yet.
            </p>
          </div>
        )}

        {/* Bottom count */}
        <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-6">
          <span className="text-white/20 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Showing {filtered.length} of {galleryItems.length} projects
          </span>
          <span className="text-white/20 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Nova AI Digital LLC
          </span>
        </div>
      </section>

      {/* Big closing word — Brutal Gallery style */}
      <div className="overflow-hidden border-t border-white/10 px-6 py-8">
        <p
          className="text-white font-black leading-none select-none"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 'clamp(80px, 18vw, 260px)',
            letterSpacing: '-0.06em',
            lineHeight: 0.85,
            color: 'rgba(255,255,255,0.04)',
          }}
        >
          NOVA
        </p>
      </div>

      <PalmerFooter />
    </div>
  );
}
